import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import Navbar     from '../components/Navbar';
import StatsCard  from '../components/StatsCard';
import SchoolCard from '../components/SchoolCard';
import BookingCard from '../components/BookingCard';
import { schoolsApi, adminApi, bookingsApi, settingsApi } from '../services/api';

const NAV = [
  { id: 'overview',  icon: '📊', label: 'Overview' },
  { id: 'schools',   icon: '🏫', label: 'Schools' },
  { id: 'bookings',  icon: '📅', label: 'All Bookings' },
  { id: 'users',     icon: '👥', label: 'Users' },
  { id: 'settings',  icon: '⚙️', label: 'Settings' },
];

const FACILITIES = ['Playground','Library','Cafeteria','Nap Room','Art Studio','Music Room','Swimming Pool','Sports Field'];
const EMPTY_SCHOOL = { name:'', address:'', phone:'', email:'', description:'', capacity:'30', operatingHours:'8:00 AM – 5:00 PM', ageRange:'0–5 years', pricePerHour:'', facilities:[], status:'active', locationUrl:'', liveStreamUrl:'' };
const EMPTY_USER   = { name:'', phone:'', userType:'school', schoolId:'' };
const PAGE_TITLE   = { overview:'Overview', schools:'Schools', bookings:'All Bookings', users:'Users', settings:'Settings', 'add-school':'Add School', 'edit-school':'Edit School', 'add-user':'Create User', 'confirm-delete':'Delete School', 'edit-user':'Edit User', 'confirm-delete-user':'Delete User' };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab,      setTab]      = useState('overview');
  const [stats,    setStats]    = useState({});
  const [schools,  setSchools]  = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(o => !o);

  // inline form state (no modals for add/edit)
  const [editingSchool, setEditingSchool] = useState(null);
  const [schoolForm,    setSchoolForm]    = useState({ ...EMPTY_SCHOOL });
  const [schoolFormErr, setSchoolFormErr] = useState('');
  const [gallery,       setGallery]       = useState([]);
  const galleryFolderRef = useRef('');
  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [userForm,      setUserForm]      = useState({ ...EMPTY_USER });
  const [userFormErr,   setUserFormErr]   = useState('');
  const [userPwTarget,  setUserPwTarget]  = useState(null);
  const [userPwForm,    setUserPwForm]    = useState({ name: '', phone: '', userType: 'school', schoolId: '', password: '', confirmPassword: '', status: 'active' });
  const [userPwErr,     setUserPwErr]     = useState('');
  const [userDeleteTarget, setUserDeleteTarget] = useState(null);
  // filters
  const [bookingFilter, setBookingFilter] = useState('all');
  const [userSearch,    setUserSearch]    = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [schoolSearch,  setSchoolSearch]  = useState('');

  // settings – twilio
  const EMPTY_TWILIO = { accountSid: '', authToken: '', whatsappFrom: '', sandboxJoinCode: '', enabled: false };
  const [twilio,        setTwilio]        = useState(EMPTY_TWILIO);
  const [twilioLoading, setTwilioLoading] = useState(false);
  const [twilioSaving,  setTwilioSaving]  = useState(false);
  const [twilioMsg,     setTwilioMsg]     = useState('');
  const [showToken,     setShowToken]     = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const d = await adminApi.getStats({ userId: user.userId, userType: 'admin' });
      setStats(d);
    } catch (_) {}
  }, [user]);

  const loadSchools = useCallback(async () => {
    setLoading(true);
    try { const d = await schoolsApi.getAll('all'); setSchools(d.schools || []); }
    catch (_) {}
    setLoading(false);
  }, []);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { userId: user.userId, userType: 'admin' };
      if (bookingFilter !== 'all') params.status = bookingFilter;
      const d = await bookingsApi.getAll(params);
      setBookings(d.bookings || []);
    } catch (_) {}
    setLoading(false);
  }, [user, bookingFilter]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try { const d = await adminApi.getUsers(user.userId); setUsers(d.users || []); }
    catch (_) {}
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadStats();
    if (tab === 'overview') loadStats();
    if (tab === 'schools')  loadSchools();
    if (tab === 'bookings') loadBookings();
    if (tab === 'users')    loadUsers();
    if (tab === 'settings') {
      setTwilioLoading(true); setTwilioMsg('');
      settingsApi.get(user.userId)
        .then(d => setTwilio({ ...EMPTY_TWILIO, ...d.settings }))
        .catch(() => {})
        .finally(() => setTwilioLoading(false));
    }
  }, [tab, bookingFilter]); // eslint-disable-line

  // ── School CRUD ──────────────────────────────────────────────────────────────
  const handleGalleryFiles = (files) => {
    setSchoolFormErr('');
    const validItems = gallery.filter(g => !g.error);
    const remaining  = 15 - validItems.length;
    const toProcess  = Array.from(files).slice(0, remaining);
    const sizeErrors = [];

    for (const file of toProcess) {
      if (!file.type.startsWith('image/')) { sizeErrors.push(`${file.name}: not an image`); continue; }
      if (file.size > 1_048_576)           { sizeErrors.push(`${file.name}: exceeds 1 MB`);  continue; }

      const localPreview = URL.createObjectURL(file);
      const folder       = galleryFolderRef.current;
      setGallery(g => [...g, { url: null, uploading: true, error: null, localPreview }]);

      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const res = await schoolsApi.uploadImage({
            adminId:   user.userId,
            imageData: ev.target.result,
            filename:  file.name,
            folder,
          });
          setGallery(g => g.map(item =>
            item.localPreview === localPreview ? { ...item, url: res.url, uploading: false } : item
          ));
        } catch (err) {
          setGallery(g => g.map(item =>
            item.localPreview === localPreview
              ? { ...item, uploading: false, error: err.message || 'Upload failed' }
              : item
          ));
        }
      };
      reader.readAsDataURL(file);
    }
    if (sizeErrors.length) setSchoolFormErr(sizeErrors.join(' · '));
  };

  const removeGalleryItem = (idx) => setGallery(g => g.filter((_, i) => i !== idx));

  const saveSchool = async () => {
    setSchoolFormErr('');
    if (!schoolForm.name || !schoolForm.address || !schoolForm.phone || !schoolForm.email) {
      setSchoolFormErr('Name, address, phone and email are required');
      return;
    }
    if (!schoolForm.locationUrl) { setSchoolFormErr('Google Location URL is required'); return; }
    if (!schoolForm.liveStreamUrl) { setSchoolFormErr('Live Streaming URL is required'); return; }
    if (gallery.some(g => g.uploading)) { setSchoolFormErr('Please wait for images to finish uploading'); return; }
    const uploadedGallery = gallery.filter(g => g.url && !g.error);
    if (uploadedGallery.length === 0) { setSchoolFormErr('At least 1 gallery image is required'); return; }
    setSaving(true);
    try {
      const data = { ...schoolForm, capacity: Number(schoolForm.capacity), pricePerHour: Number(schoolForm.pricePerHour || 0), gallery: uploadedGallery.map(g => g.url) };
      if (editingSchool) {
        await schoolsApi.update({ ...data, schoolId: editingSchool.id, adminId: user.userId });
      } else {
        await schoolsApi.create({ ...data, adminId: user.userId });
      }
      setEditingSchool(null);
      setSchoolForm({ ...EMPTY_SCHOOL });
      setGallery([]);
      galleryFolderRef.current = '';
      setTab('schools');
      loadSchools(); loadStats();
    } catch (err) { setSchoolFormErr(err.message); }
    setSaving(false);
  };

  const deleteSchool = async () => {
    setSaving(true);
    try {
      await schoolsApi.remove(deleteTarget.id, user.userId);
      setDeleteTarget(null);
      setTab('schools');
      loadSchools(); loadStats();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  // ── Create user ─────────────────────────────────────────────────────────────
  const createUser = async () => {
    setUserFormErr('');
    if (!userForm.name || !userForm.phone) { setUserFormErr('Name and phone are required'); return; }
    const phone = userForm.phone.replace(/[^\d]/g, '');
    if (phone.length !== 10) { setUserFormErr('Enter a valid 10-digit phone number'); return; }
    setSaving(true);
    try {
      await adminApi.createUser({ ...userForm, adminId: user.userId });
      setUserForm({ ...EMPTY_USER });
      setTab('users');
      loadUsers();
    } catch (err) { setUserFormErr(err.message); }
    setSaving(false);
  };

  const deleteUser = async () => {
    setSaving(true);
    try {
      await adminApi.deleteUser({ adminId: user.userId, userId: userDeleteTarget.id });
      setUserDeleteTarget(null);
      setTab('users');
      loadUsers();
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const updateUserPassword = async () => {
    setUserPwErr('');
    if (userPwForm.password && userPwForm.password.length < 6) { setUserPwErr('Password must be at least 6 characters'); return; }
    if (userPwForm.password && userPwForm.password !== userPwForm.confirmPassword) { setUserPwErr('Passwords do not match'); return; }
    const ph = userPwForm.phone.replace(/[^\d]/g,'');
    if (ph && ph.length !== 10) { setUserPwErr('Phone must be 10 digits'); return; }
    setSaving(true);
    try {
      const payload = { adminId: user.userId, userId: userPwTarget.id };
      if (userPwForm.name.trim())                                payload.name     = userPwForm.name.trim();
      if (ph.length === 10)                                      payload.phone    = ph;
      if (userPwForm.userType)                                   payload.userType = userPwForm.userType;
      if (userPwForm.schoolId !== undefined)                     payload.schoolId = userPwForm.schoolId;
      if (userPwForm.password)                                   payload.password = userPwForm.password;
      if (userPwForm.status !== userPwTarget.status)             payload.status   = userPwForm.status;
      await adminApi.updateUser(payload);
      setUserPwTarget(null);
      setUserPwForm({ name: '', phone: '', userType: 'school', schoolId: '', password: '', confirmPassword: '', status: 'active' });
      setTab('users');
      loadUsers();
    } catch (err) { setUserPwErr(err.message); }
    setSaving(false);
  };

  // ── Filter helpers ─────────────────────────────────────────────────────────
  const filteredSchools = schools.filter(s =>
    s.name?.toLowerCase().includes(schoolSearch.toLowerCase()) ||
    s.address?.toLowerCase().includes(schoolSearch.toLowerCase())
  );
  const filteredUsers = users.filter(u => {
    const matchRole = userRoleFilter === 'all' || u.userType === userRoleFilter;
    const q = userSearch.toLowerCase();
    const matchSearch = !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.toLowerCase().includes(q);
    return matchRole && matchSearch;
  });

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="dash-shell">
      {/* Mobile backdrop */}
      <div className={`sidebar-backdrop${sidebarOpen ? ' visible' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Flogo_la.png?alt=media"
            alt="LittleAura"
            className="sidebar-logo-img"
          />
          <div className="logo-sub">Admin Console</div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section-label">Main Menu</div>
          {NAV.map(n => (
            <div key={n.id} className={`nav-item${tab === n.id ? ' active' : ''}`} onClick={() => { setTab(n.id); setSidebarOpen(false); }}>
              <span className="nav-icon">{n.icon}</span>{n.label}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar" style={{ background: '#7c3aed' }}>{initials}</div>
            <div className="sidebar-user-info">
              <div className="name">{user?.name}</div>
              <div className="role">Administrator</div>
            </div>
          </div>
          <button className="btn btn-outline btn-sm btn-full" style={{ color: 'rgba(255,255,255,.6)', borderColor: 'rgba(255,255,255,.15)' }} onClick={logout}>Sign Out</button>
        </div>
      </aside>

      {/* Main */}
      <div className="dash-main">
        <Navbar
          title={PAGE_TITLE[tab] || 'Dashboard'}
          subtitle={tab === 'overview' ? 'Welcome back, ' + user?.name : undefined}
          onMenuToggle={toggleSidebar}
        />

        <div className="dash-content">

          {/* ── OVERVIEW ─────────────────────────────────────────────── */}
          {tab === 'overview' && (
            <>
              <p className="page-sub">Here's what's happening today in your daycare network.</p>
              <div className="stats-grid">
                <StatsCard icon="🏫" label="Total Schools"     value={stats.totalSchools}      color="blue"   />
                <StatsCard icon="👥" label="Total Users"       value={stats.totalUsers}        color="purple" />
                <StatsCard icon="📅" label="Total Bookings"    value={stats.totalBookings}     color="teal"   />
                <StatsCard icon="⏳" label="Pending"           value={stats.pendingBookings}   color="yellow" />
                <StatsCard icon="✅" label="Accepted"          value={stats.acceptedBookings}  color="green"  />
                <StatsCard icon="❌" label="Cancelled"         value={stats.cancelledBookings} color="red"    />
              </div>

              <div className="section mt-16">
                <div className="section-header">
                  <h3>Quick Actions</h3>
                </div>
                <div className="section-body" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={() => { setEditingSchool(null); setSchoolForm({ ...EMPTY_SCHOOL }); setGallery([]); galleryFolderRef.current = `schools/new_${Date.now()}`; setTab('add-school'); }}>+ Add School</button>
                  <button className="btn btn-outline" onClick={() => { setUserForm({ ...EMPTY_USER }); setTab('add-user'); }}>+ Create User</button>
                  <button className="btn btn-outline" onClick={() => setTab('bookings')}>View All Bookings</button>
                </div>
              </div>
            </>
          )}

          {/* ── SCHOOLS ──────────────────────────────────────────────── */}
          {tab === 'schools' && (
            <>
              <div className="flex-between mb-24 flex-between-mobile">
                <div>
                  <p className="page-title">Schools</p>
                  <p className="page-sub" style={{ marginBottom: 0 }}>Manage your daycare network</p>
                </div>
                <button className="btn btn-primary" onClick={() => { setEditingSchool(null); setSchoolForm({ ...EMPTY_SCHOOL }); setGallery([]); galleryFolderRef.current = `schools/new_${Date.now()}`; setTab('add-school'); }}>
                  + Add School
                </button>
              </div>

              <div className="filter-bar mb-16">
                <input className="search-input" placeholder="🔍 Search schools…" value={schoolSearch} onChange={e => setSchoolSearch(e.target.value)} />
              </div>

              {loading ? <div className="spinner" /> : filteredSchools.length === 0 ? (
                <div className="empty-state"><span className="empty-icon">🏫</span><h3>No schools found</h3><p>Add your first school to get started.</p></div>
              ) : (
                <div className="school-grid">
                  {filteredSchools.map(s => (
                    <SchoolCard key={s.id} school={s} actions={[
                      { label: '\u270f\ufe0f Edit',   className: 'btn-outline', onClick: (sc) => { setEditingSchool(sc); setSchoolForm({ name: sc.name||'', address: sc.address||'', phone: sc.phone||'', email: sc.email||'', description: sc.description||'', capacity: String(sc.capacity||30), operatingHours: sc.operatingHours||'8:00 AM \u2013 5:00 PM', ageRange: sc.ageRange||'0\u20135 years', pricePerHour: String(sc.pricePerHour||sc.pricePerDay||''), facilities: sc.facilities||[], status: sc.status||'active', locationUrl: sc.locationUrl||'', liveStreamUrl: sc.liveStreamUrl||'' }); setGallery((sc.gallery||[]).map(url=>({url, uploading:false, error:null, localPreview:url}))); galleryFolderRef.current = `schools/${sc.id||sc.schoolId||'edit_'+Date.now()}`; setTab('edit-school'); } },
                      { label: '🗑️ Delete', className: 'btn-danger',  onClick: (sc) => { setDeleteTarget(sc); setTab('confirm-delete'); } },
                    ]} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── BOOKINGS ─────────────────────────────────────────────── */}
          {tab === 'bookings' && (
            <>
              <p className="page-title">All Bookings</p>
              <div className="tabs mb-16">
                {['all','pending','accepted','cancelled'].map(s => {
                  const cnt = s === 'all' ? bookings.length : bookings.filter(b => b.status === s).length;
                  return (
                    <button key={s} className={`tab-btn${bookingFilter === s ? ' active' : ''}`} onClick={() => setBookingFilter(s)}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}<span className="tab-count">{cnt}</span>
                    </button>
                  );
                })}
              </div>

              {loading ? <div className="spinner" /> : bookings.length === 0 ? (
                <div className="empty-state"><span className="empty-icon">📅</span><h3>No bookings yet</h3></div>
              ) : (
                <div className="booking-grid">
                  {bookings.map(b => <BookingCard key={b.id} booking={b} variant="admin" />)}
                </div>
              )}
            </>
          )}

          {/* ── USERS ────────────────────────────────────────────────── */}
          {tab === 'users' && (
            <>
              <div className="flex-between mb-16">
                <p className="page-title">Users</p>
                <button className="btn btn-primary" onClick={() => { setUserForm({ ...EMPTY_USER }); setTab('add-user'); }}>+ Create User</button>
              </div>

              <div className="filter-bar mb-16" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="form-select" style={{ width: 'auto', minWidth: 140 }} value={userRoleFilter} onChange={e => setUserRoleFilter(e.target.value)}>
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="school">School</option>
                  <option value="parent">Parent</option>
                </select>
                <input className="search-input" style={{ flex: 1, minWidth: 180 }} placeholder="🔍 Search by name, email or phone…" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              </div>

              {loading ? <div className="spinner" /> : (
                <div className="section">
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(u => (
                          <tr key={u.id}>
                            <td><div className="flex-gap"><div className="avatar avatar-sm" style={{ background: u.userType === 'admin' ? '#7c3aed' : u.userType === 'school' ? '#0369a1' : '#d97706', flexShrink: 0 }}>{u.name?.charAt(0)}</div>{u.name}</div></td>
                            <td>{u.email}</td>
                            <td><span className={`badge badge-${u.userType}`}>{u.userType}</span></td>
                            <td><span className={`badge badge-${u.status}`}>{u.status || 'active'}</span></td>
                            <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{u.createdAt?.slice(0, 10) || '—'}</td>
                            <td style={{ display: 'flex', gap: 6 }}>
                              <button className="btn btn-outline btn-sm" onClick={() => { setUserPwTarget(u); setUserPwForm({ name: u.name||'', phone: u.phone||'', userType: u.userType||'parent', schoolId: u.schoolId||'', password: '', confirmPassword: '', status: u.status || 'active' }); setTab('edit-user'); }}>✏️ Edit</button>
                              {u.id !== user.userId && <button className="btn btn-danger btn-sm" onClick={() => { setUserDeleteTarget(u); setTab('confirm-delete-user'); }}>🗑️</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredUsers.length === 0 && <div className="empty-state" style={{ padding: 32 }}><span>No users found</span></div>}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── ADD / EDIT SCHOOL (inline page) ──────────────────── */}
          {(tab === 'add-school' || tab === 'edit-school') && (
            <div className="form-page">
              <button className="btn btn-ghost back-btn" onClick={() => { setEditingSchool(null); setSchoolFormErr(''); setTab('schools'); }}>
                ← Back to Schools
              </button>
              <h2 className="form-page-title">{tab === 'edit-school' ? '✏️ Edit School' : '🏫 Add New School'}</h2>

              {schoolFormErr && <div className="form-error mb-16">{schoolFormErr}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">School Name *</label>
                  <input className="form-input" value={schoolForm.name} onChange={e => setSchoolForm(f => ({...f, name: e.target.value}))} placeholder="Sunshine Daycare" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" value={schoolForm.email} onChange={e => setSchoolForm(f => ({...f, email: e.target.value}))} placeholder="school@email.com" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Address *</label>
                <input className="form-input" value={schoolForm.address} onChange={e => setSchoolForm(f => ({...f, address: e.target.value}))} placeholder="123 Main St, City" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <input className="form-input" value={schoolForm.phone} onChange={e => setSchoolForm(f => ({...f, phone: e.target.value}))} placeholder="+1 555-000-0000" />
                </div>
                <div className="form-group">
                  <label className="form-label">Charge Per Hour (₹)</label>
                  <input className="form-input" type="number" value={schoolForm.pricePerHour} onChange={e => setSchoolForm(f => ({...f, pricePerHour: e.target.value}))} placeholder="50" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Capacity</label>
                  <input className="form-input" type="number" value={schoolForm.capacity} onChange={e => setSchoolForm(f => ({...f, capacity: e.target.value}))} placeholder="30" />
                </div>
                <div className="form-group">
                  <label className="form-label">Age Range</label>
                  <input className="form-input" value={schoolForm.ageRange} onChange={e => setSchoolForm(f => ({...f, ageRange: e.target.value}))} placeholder="0–5 years" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Operating Hours</label>
                  <input className="form-input" value={schoolForm.operatingHours} onChange={e => setSchoolForm(f => ({...f, operatingHours: e.target.value}))} placeholder="8:00 AM – 5:00 PM" />
                </div>
                <div className="form-group">
                  <label className="form-label">Account Status</label>
                  <select className="form-select" value={schoolForm.status} onChange={e => setSchoolForm(f => ({...f, status: e.target.value}))}>
                    <option value="active">✅ Active — can login</option>
                    <option value="inactive">🚫 Disabled — cannot login</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={3} value={schoolForm.description} onChange={e => setSchoolForm(f => ({...f, description: e.target.value}))} placeholder="A brief description of the school…" />
              </div>

              <div className="form-group">
                <label className="form-label">Facilities</label>
                <div className="facilities-grid">
                  {FACILITIES.map(fac => (
                    <label key={fac} className={`facility-pill${schoolForm.facilities.includes(fac) ? ' selected' : ''}`}>
                      <input type="checkbox" style={{ display: 'none' }}
                        checked={schoolForm.facilities.includes(fac)}
                        onChange={() => setSchoolForm(f => ({
                          ...f,
                          facilities: f.facilities.includes(fac) ? f.facilities.filter(x => x !== fac) : [...f.facilities, fac]
                        }))}
                      />
                      {fac}
                    </label>
                  ))}
                </div>
              </div>
              {/* ── Location URL ── */}
              <div className="form-group">
                <label className="form-label">Google Location URL *</label>
                <div className="input-icon-wrap">
                  <span className="input-icon">📍</span>
                  <input
                    className="form-input input-with-icon"
                    type="url"
                    value={schoolForm.locationUrl}
                    onChange={e => setSchoolForm(f => ({...f, locationUrl: e.target.value}))}
                    placeholder="https://maps.google.com/?q=..."
                  />
                </div>
                <p className="form-hint">Paste the Google Maps share link for this school.</p>
              </div>

              {/* ── Live Stream URL ── */}
              <div className="form-group">
                <label className="form-label">Live Streaming URL *</label>
                <div className="input-icon-wrap">
                  <span className="input-icon">🎥</span>
                  <input
                    className="form-input input-with-icon"
                    type="url"
                    value={schoolForm.liveStreamUrl}
                    onChange={e => setSchoolForm(f => ({...f, liveStreamUrl: e.target.value}))}
                    placeholder="https://youtube.com/watch?v=…"
                  />
                </div>
                <p className="form-hint">YouTube, Vimeo, or any public live-stream link.</p>
              </div>

              {/* ── Gallery ── */}
              <div className="form-group">
                <label className="form-label">School Gallery (1–15 photos) *</label>
                <p className="form-hint" style={{marginBottom: 10}}>Each image must be under 1 MB · JPG, PNG, WebP supported</p>

                {gallery.length > 0 && (
                  <div className="gallery-grid">
                    {gallery.map((item, idx) => (
                      <div key={idx} className={`gallery-thumb${item.error ? ' gallery-thumb--error' : item.uploading ? ' gallery-thumb--uploading' : ' gallery-thumb--done'}`}>
                        <img src={item.localPreview} alt={`Gallery ${idx + 1}`} className="gallery-thumb-img" />
                        {item.uploading && (
                          <div className="gallery-overlay">
                            <div className="gallery-spinner" />
                          </div>
                        )}
                        {item.error && (
                          <div className="gallery-overlay gallery-overlay--error">
                            <span className="gallery-error-icon">⚠</span>
                          </div>
                        )}
                        <button
                          className="gallery-remove"
                          type="button"
                          onClick={() => removeGalleryItem(idx)}
                          title={item.error ? 'Remove' : 'Remove image'}
                          disabled={item.uploading}
                        >×</button>
                        {item.error && <div className="gallery-error-msg">{item.error}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {gallery.filter(g => !g.error).length < 15 && (
                  <label className="gallery-dropzone" htmlFor="gallery-file-input">
                    <input
                      id="gallery-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      multiple
                      style={{display: 'none'}}
                      onChange={e => { handleGalleryFiles(e.target.files); e.target.value = ''; }}
                    />
                    <span className="gallery-dropzone-icon">📷</span>
                    <span className="gallery-dropzone-label">Click to add photos</span>
                    <span className="gallery-dropzone-count">{gallery.filter(g => !g.error).length} / 15 added</span>
                  </label>
                )}
              </div>

              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => { setEditingSchool(null); setSchoolFormErr(''); setGallery([]); setTab('schools'); }}>Cancel</button>
                <button className="btn btn-primary" onClick={saveSchool} disabled={saving}>
                  {saving ? 'Saving…' : tab === 'edit-school' ? 'Save Changes' : 'Create School'}
                </button>
              </div>
            </div>
          )}

          {/* ── ADD USER (inline page) ────────────────────────────── */}
          {tab === 'add-user' && (
            <div className="form-page">
              <button className="btn btn-ghost back-btn" onClick={() => { setUserFormErr(''); setTab('users'); }}>
                ← Back to Users
              </button>
              <h2 className="form-page-title">👤 Create New User</h2>

              {userFormErr && <div className="form-error mb-16">{userFormErr}</div>}

              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={userForm.name} onChange={e => setUserForm(f => ({...f, name: e.target.value}))} placeholder="Jane Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile Number *</label>
                <input className="form-input" type="tel" inputMode="numeric" value={userForm.phone} onChange={e => setUserForm(f => ({...f, phone: e.target.value.replace(/[^\d]/g,'').slice(0,10)}))} placeholder="10-digit number" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={userForm.userType} onChange={e => setUserForm(f => ({...f, userType: e.target.value}))}>
                    <option value="parent">Parent</option>
                    <option value="school">School User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {userForm.userType === 'school' && (
                  <div className="form-group">
                    <label className="form-label">School ID</label>
                    <input className="form-input" value={userForm.schoolId} onChange={e => setUserForm(f => ({...f, schoolId: e.target.value}))} placeholder="School document ID" />
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => { setUserFormErr(''); setTab('users'); }}>Cancel</button>
                <button className="btn btn-primary" onClick={createUser} disabled={saving}>
                  {saving ? 'Creating…' : 'Create User'}
                </button>
              </div>
            </div>
          )}
          {/* ── SET PASSWORD (inline page) ────────────────── */}
          {tab === 'edit-user' && userPwTarget && (
            <div className="form-page">
              <button className="btn btn-ghost back-btn" onClick={() => { setUserPwTarget(null); setUserPwErr(''); setTab('users'); }}>
                ← Back to Users
              </button>
              <h2 className="form-page-title">✏️ Edit User</h2>

              <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '16px', marginBottom: 24 }}>
                <div style={{ fontWeight: 600 }}>{userPwTarget.name}</div>
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>ID: {userPwTarget.id}</div>
              </div>

              {userPwErr && <div className="form-error mb-16">{userPwErr}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={userPwForm.name} onChange={e => setUserPwForm(f => ({...f, name: e.target.value}))} placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input className="form-input" type="tel" inputMode="numeric" value={userPwForm.phone} onChange={e => setUserPwForm(f => ({...f, phone: e.target.value.replace(/[^\d]/g,'').slice(0,10)}))} placeholder="10-digit number" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={userPwForm.userType} onChange={e => setUserPwForm(f => ({...f, userType: e.target.value}))}>
                    <option value="parent">Parent</option>
                    <option value="school">School</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">School ID {userPwForm.userType !== 'school' ? '(school users only)' : '*'}</label>
                  <input className="form-input" value={userPwForm.schoolId} onChange={e => setUserPwForm(f => ({...f, schoolId: e.target.value}))} placeholder="School document ID" disabled={userPwForm.userType !== 'school'} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Account Status</label>
                <select className="form-select" value={userPwForm.status} onChange={e => setUserPwForm(f => ({...f, status: e.target.value}))}>
                  <option value="active">✅ Active — can login</option>
                  <option value="inactive">🚫 Disabled — cannot login</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">New Password <span style={{color:'var(--gray-400)',fontWeight:400}}>(leave blank to keep current)</span></label>
                <input className="form-input" type="password" value={userPwForm.password} onChange={e => setUserPwForm(f => ({...f, password: e.target.value}))} placeholder="Min. 6 characters" />
              </div>
              {userPwForm.password && (
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input className="form-input" type="password" value={userPwForm.confirmPassword} onChange={e => setUserPwForm(f => ({...f, confirmPassword: e.target.value}))} placeholder="Re-enter new password" />
                </div>
              )}
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => { setUserPwTarget(null); setUserPwErr(''); setTab('users'); }}>Cancel</button>
                <button className="btn btn-primary" onClick={updateUserPassword} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          )}
          {/* ── SETTINGS ──────────────────────────────────────── */}
          {tab === 'settings' && (
            <div style={{ maxWidth: 620 }}>
              <p className="page-sub" style={{ marginBottom: 24 }}>Manage integration credentials for your platform.</p>

              <div className="section">
                <div className="section-header">
                  <h3>💬 Twilio WhatsApp Credentials</h3>
                </div>
                <div className="section-body" style={{ padding: '20px 24px' }}>
                  <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 20 }}>
                    Used to send WhatsApp notifications to parents when their booking status changes.
                  </p>

                  {twilioLoading ? <div className="spinner" /> : (
                    <>
                      <div className="form-group">
                        <label className="form-label">Enable WhatsApp Notifications</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 4 }}>
                          <input type="checkbox" checked={!!twilio.enabled}
                            onChange={e => setTwilio(t => ({ ...t, enabled: e.target.checked }))}
                            style={{ width: 18, height: 18, accentColor: 'var(--primary)', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 14, color: 'var(--gray-700)' }}>
                            {twilio.enabled ? '✅ Enabled' : '⚫ Disabled'}
                          </span>
                        </label>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Account SID</label>
                        <input className="form-input"
                          value={twilio.accountSid}
                          onChange={e => setTwilio(t => ({ ...t, accountSid: e.target.value }))}
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          style={{ fontFamily: 'monospace', fontSize: 13 }}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Auth Token</label>
                        <div style={{ position: 'relative' }}>
                          <input className="form-input"
                            type={showToken ? 'text' : 'password'}
                            value={twilio.authToken}
                            onChange={e => setTwilio(t => ({ ...t, authToken: e.target.value }))}
                            placeholder="Your Twilio Auth Token"
                            style={{ fontFamily: 'monospace', fontSize: 13, paddingRight: 72 }}
                          />
                          <button type="button"
                            onClick={() => setShowToken(s => !s)}
                            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>
                            {showToken ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">WhatsApp From Number</label>
                        <input className="form-input"
                          value={twilio.whatsappFrom}
                          onChange={e => setTwilio(t => ({ ...t, whatsappFrom: e.target.value }))}
                          placeholder="whatsapp:+14155238886"
                          style={{ fontFamily: 'monospace', fontSize: 13 }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4, display: 'block' }}>
                          Use your Twilio Sandbox number or approved WhatsApp sender (include the <code>whatsapp:</code> prefix).
                        </span>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Sandbox Join Code</label>
                        <input className="form-input"
                          value={twilio.sandboxJoinCode}
                          onChange={e => setTwilio(t => ({ ...t, sandboxJoinCode: e.target.value }))}
                          placeholder="e.g. flies-spell"
                          style={{ fontFamily: 'monospace', fontSize: 13 }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4, display: 'block' }}>
                          The join word shown on your Twilio Sandbox page. New parents must send this to activate WhatsApp.
                        </span>
                      </div>

                      {twilioMsg && (
                        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                          background: twilioMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2',
                          color: twilioMsg.startsWith('✅') ? '#166534' : '#991b1b', fontSize: 14 }}>
                          {twilioMsg}
                        </div>
                      )}

                      <div className="form-actions">
                        <button className="btn btn-primary" disabled={twilioSaving}
                          onClick={async () => {
                            setTwilioSaving(true); setTwilioMsg('');
                            try {
                              await settingsApi.save({ adminId: user.userId, ...twilio });
                              setTwilioMsg('✅ Credentials saved successfully!');
                            } catch (err) {
                              setTwilioMsg('❌ ' + err.message);
                            }
                            setTwilioSaving(false);
                          }}>
                          {twilioSaving ? 'Saving…' : 'Save Credentials'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── CONFIRM DELETE USER (inline page) ──────────── */}
          {tab === 'confirm-delete-user' && userDeleteTarget && (
            <div className="form-page">
              <button className="btn btn-ghost back-btn" onClick={() => { setUserDeleteTarget(null); setTab('users'); }}>← Back to Users</button>
              <h2 className="form-page-title">🗑️ Delete User</h2>
              <div className="confirm-danger-box">
                <p>Are you sure you want to delete <strong>{userDeleteTarget.name}</strong> ({userDeleteTarget.phone})?</p>
                <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8 }}>This action is permanent and cannot be undone.</p>
              </div>
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => { setUserDeleteTarget(null); setTab('users'); }}>Cancel</button>
                <button className="btn btn-danger" onClick={deleteUser} disabled={saving}>{saving ? 'Deleting…' : 'Yes, Delete User'}</button>
              </div>
            </div>
          )}

          {/* ── CONFIRM DELETE (inline page) ─────────────────── */}
          {tab === 'confirm-delete' && deleteTarget && (
            <div className="form-page">
              <button className="btn btn-ghost back-btn" onClick={() => { setDeleteTarget(null); setTab('schools'); }}>
                ← Back to Schools
              </button>
              <h2 className="form-page-title">🗑️ Delete School</h2>
              <div className="confirm-danger-box">
                <p>Are you sure you want to delete <strong>{deleteTarget.name}</strong>?</p>
                <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8 }}>This action is permanent and cannot be undone.</p>
              </div>
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => { setDeleteTarget(null); setTab('schools'); }}>Cancel</button>
                <button className="btn btn-danger" onClick={deleteSchool} disabled={saving}>{saving ? 'Deleting…' : 'Yes, Delete School'}</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
